import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useMutation, useQueryClient } from 'react-query';
import { Translate } from '../../../../../../../shared/LanguageService';
import SuccessSnackbar from '../../../../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../../../../shared/feedback/FailureSnackbar';
import { IImageThumbnail, ICreateStitchedImageResponse } from '../../../Entities';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { useStitchImageOffset } from '../state/use-stitch-image-offset';
import { PREVIEW_HEIGHT, PREVIEW_WIDTH } from '../state/utils';
import { StitchImagePreview } from './StitchImagePreview';
import { GetImagesResponse, imagesQueryKey } from '../../listImages/queries/use-query-images';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';

type CreateStitchedImageDialogProps = {
    open: boolean;
    onClose: () => void;
    leftImage: IImageThumbnail | null;
    rightImage: IImageThumbnail | null;
    isLoading: boolean;
    onCreate: (leftOffset: number, rightOffset: number) => void;
};

export function CreateStitchedImageDialog({
    open,
    onClose,
    leftImage,
    rightImage,
    isLoading,
    onCreate
}: CreateStitchedImageDialogProps) {
    const leftOffset = useStitchImageOffset({
        highResolutionWidth: leftImage ? leftImage.highResolutionWidth : 0,
        highResolutionHeight: leftImage ? leftImage.highResolutionHeight : 0,
        previewWidth: PREVIEW_WIDTH,
        previewHeight: PREVIEW_HEIGHT
    });

    const rightOffset = useStitchImageOffset({
        highResolutionWidth: rightImage ? rightImage.highResolutionWidth : 0,
        highResolutionHeight: rightImage ? rightImage.highResolutionHeight : 0,
        previewWidth: PREVIEW_WIDTH,
        previewHeight: PREVIEW_HEIGHT
    });

    function handleCreate() {
        onCreate(leftOffset.realPixelOffset, rightOffset.realPixelOffset);
    }

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth={false}
            PaperProps={{
                sx: { width: 'auto', maxWidth: 'none', backgroundColor: '#dde3e3' }
            }}
            TransitionProps={{
                onEnter: function () {
                    leftOffset.resetOffset();
                    rightOffset.resetOffset();
                }
            }}
        >
            <DialogTitle>{Translate('image-processing/stitch-images/modal/title')}</DialogTitle>
            <DialogContent>
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        backgroundColor: '#b8cec8',
                        color: '#1a2e2a',
                        '& .MuiAlert-icon': { color: '#3a6b5e' }
                    }}
                >
                    {Translate('image-processing/stitch-images/modal/info')}
                </Alert>
                <Box sx={{ display: 'flex', gap: '6px' }}>
                    {leftImage && (
                        <StitchImagePreview
                            image={leftImage}
                            alt="Left stitch image"
                            dragBoundY={leftOffset.dragBoundY}
                            onDragStop={leftOffset.handleDragStop}
                        />
                    )}
                    {rightImage && (
                        <StitchImagePreview
                            image={rightImage}
                            alt="Right stitch image"
                            dragBoundY={rightOffset.dragBoundY}
                            onDragStop={rightOffset.handleDragStop}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading} aria-busy={isLoading}>
                    {Translate('image-processing/stitch-images/modal/cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    disabled={isLoading}
                    aria-busy={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                >
                    {Translate('image-processing/stitch-images/modal/create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type DataLayerProps = {
    open: boolean;
    onClose: () => void;
    leftImage: IImageThumbnail | null;
    rightImage: IImageThumbnail | null;
    orderId: number;
    onCreated: () => void;
};

export default function DataLayer({
    open,
    onClose,
    leftImage,
    rightImage,
    orderId,
    onCreated
}: DataLayerProps) {
    const queryClient = useQueryClient();
    const [showSuccess, setShowSuccess] = React.useState(false);
    let [error, setError] = React.useState<{ messages: string[] } | null>(null);

    const mutation = useMutation(
        (offsets: { leftOffset: number; rightOffset: number }) => {
            if (!leftImage || !rightImage) {
                return Promise.reject(new Error('Missing images'));
            }

            return ImageProcessingApi.createStitchedImage(
                leftImage.id,
                offsets.leftOffset,
                rightImage.id,
                offsets.rightOffset,
                orderId,
                leftImage.orderRowId
            );
        },
        {
            onSuccess: (response: ICreateStitchedImageResponse) => {
                setShowSuccess(true);
                onClose();
                onCreated();

                // Optimistic UI: inject the new image into the cache immediately
                // so a DraggableImageCard with a loading skeleton appears right away.
                // The empty `reference` keeps ImageLoadingSkeleton visible until
                // the delayed invalidation replaces it with real data.
                queryClient.setQueryData<GetImagesResponse>(
                    [imagesQueryKey, { orderId }],
                    (oldData) => {
                        if (!oldData || !leftImage) {
                            if (!oldData) {
                                return [];
                            }

                            return oldData;
                        }

                        return oldData.map((product) => {
                            if (product.id !== leftImage.orderRowId) {
                                return product;
                            }

                            const optimisticThumbnail: IImageThumbnail = {
                                ...response.thumbnail,
                                filename: response.thumbnail.filename || response.fileName,
                                // Use empty reference so the skeleton stays visible
                                // until the query revalidates with the real thumbnail URL.
                                reference: response.thumbnail.reference || '',
                                sortIndex: 0
                            };

                            return {
                                ...product,
                                thumbnails: [optimisticThumbnail, ...product.thumbnails]
                            };
                        });
                    }
                );

                setTimeout(() => {
                    queryClient.invalidateQueries(imagesQueryKey);
                    queryClient.invalidateQueries(photoStatusQueryKey);
                }, 90);
            },
            onError: async (error) => {
                let errorMessages = [Translate('500-error/description')];
                try {
                    // @ts-ignore
                    if (error.response) {
                        // @ts-ignore
                        let res = await error.response.json();
                        errorMessages = res.errors.map((error: any) => Translate(error.value));
                    }
                } catch (e) {
                    errorMessages = [Translate('500-error/description')];
                }

                setError({ messages: errorMessages });
            }
        }
    );

    function handleCreate(leftOffset: number, rightOffset: number) {
        mutation.mutate({ leftOffset, rightOffset });
    }

    return (
        <>
            <CreateStitchedImageDialog
                open={open}
                onClose={onClose}
                leftImage={leftImage}
                rightImage={rightImage}
                isLoading={mutation.isLoading}
                onCreate={handleCreate}
            />
            <SuccessSnackbar
                open={showSuccess}
                onClose={() => {
                    setShowSuccess(false);
                }}
                text={Translate('image-processing/notification/stitch-images/success')}
            />
            <FailureSnackbar
                open={error !== null ? true : false}
                onClose={() => {
                    setError(null);
                }}
                text={
                    error !== null ? (
                        <Box
                            component="section"
                            sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            {error.messages.map((message) => (
                                <article>{message}</article>
                            ))}
                        </Box>
                    ) : null
                }
            />
        </>
    );
}
